import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from './ui/dialog';
import { FileText, Image as ImageIcon, Eye, Download, Trash2, Loader2,
  FileSpreadsheet, FileArchive, File as FileGeneric, ExternalLink
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import DocumentOcrCard from './DocumentOcrCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' });
  } catch {
    return '';
  }
};

const IMG_EXT = new Set(['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif', 'gif', 'bmp']);
const PDF_EXT = new Set(['pdf']);
const XLS_EXT = new Set(['xls', 'xlsx', 'ods', 'csv']);
const DOC_EXT = new Set(['doc', 'docx', 'odt', 'rtf', 'txt']);
const ARCH_EXT = new Set(['zip', 'rar', '7z']);

const getExt = (name = '') => name.split('.').pop().toLowerCase();

const getFileMeta = (name) => {
  const ext = getExt(name);
  if (IMG_EXT.has(ext)) return { kind: 'image', icon: ImageIcon, color: 'text-emerald-600 bg-emerald-100' };
  if (PDF_EXT.has(ext)) return { kind: 'pdf', icon: FileText, color: 'text-rose-600 bg-rose-100' };
  if (XLS_EXT.has(ext)) return { kind: 'xls', icon: FileSpreadsheet, color: 'text-green-600 bg-green-100' };
  if (DOC_EXT.has(ext)) return { kind: 'doc', icon: FileText, color: 'text-sky-600 bg-sky-100' };
  if (ARCH_EXT.has(ext)) return { kind: 'archive', icon: FileArchive, color: 'text-amber-600 bg-amber-100' };
  return { kind: 'other', icon: FileGeneric, color: 'text-slate-600 bg-slate-100' };
};

const TIPO_LABELS = {
  carta_identita: "Carta d'identita'",
  codice_fiscale: 'Codice fiscale',
  bolletta: 'Bolletta',
  contratto_firmato: 'Contratto firmato',
  visura: 'Visura',
  altro: 'Altro',
};

const ContractDocumentsPreview = ({ contractId, clientId, refreshKey = 0, onClientRefresh }) => {
  const { hasRole } = useAuth();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null); // {doc, blobUrl, kind, loading}

  const fetchDocs = useCallback(async () => {
    if (!clientId) { setLoading(false); return; }
    try {
      setLoading(true);
      const clientDocsRes = await axios.get(`${API_URL}/api/clients/${clientId}/documents`);
      const list = clientDocsRes.data || [];
      // Se contractId non e' fornito -> siamo su Scheda Cliente: mostra TUTTI i documenti
      // Se contractId e' fornito -> siamo su Scheda Pratica: solo i doc di quel contratto o senza contratto
      const filtered = contractId
        ? list.filter((d) => !d.contract_id || d.contract_id === contractId)
        : list;
      setDocs(filtered);
    } catch (e) {
      // silently ignore - not fatal
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, contractId]);

  useEffect(() => { fetchDocs(); }, [fetchDocs, refreshKey]);

  const openPreview = async (doc) => {
    const meta = getFileMeta(doc.nome_file || doc.filename);
    if (meta.kind !== 'image' && meta.kind !== 'pdf') {
      // fallback: download for non-previewable types
      handleDownload(doc);
      return;
    }
    setPreview({ doc, blobUrl: null, kind: meta.kind, loading: true });
    try {
      const url = doc.url ? `${API_URL}${doc.url}` : `${API_URL}/api/documents/${doc.id}/download`;
      const res = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: res.headers['content-type'] || (meta.kind === 'pdf' ? 'application/pdf' : 'image/jpeg') });
      const blobUrl = window.URL.createObjectURL(blob);
      setPreview({ doc, blobUrl, kind: meta.kind, loading: false });
    } catch (e) {
      toast.error('Impossibile caricare l\'anteprima');
      setPreview(null);
    }
  };

  const closePreview = () => {
    if (preview?.blobUrl) window.URL.revokeObjectURL(preview.blobUrl);
    setPreview(null);
  };

  const handleDownload = async (doc) => {
    try {
      const url = doc.url ? `${API_URL}${doc.url}` : `${API_URL}/api/documents/${doc.id}/download`;
      const res = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([res.data]);
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = doc.nome_file || doc.filename;
      a.click();
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    } catch (e) {
      toast.error('Errore download');
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Eliminare questo documento?')) return;
    try {
      await axios.delete(`${API_URL}/api/documents/${docId}`);
      toast.success('Documento eliminato');
      fetchDocs();
    } catch (e) {
      toast.error('Errore eliminazione');
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-6 flex items-center justify-center text-slate-400 text-sm">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Carico documenti…
        </CardContent>
      </Card>
    );
  }

  if (!docs.length) {
    return null; // nothing to show
  }

  const canDelete = hasRole(['super_admin', 'sub_agent', 'interno', 'back_office']);

  const OCR_TYPES = new Set(['carta_identita', 'patente', 'codice_fiscale', 'passaporto', 'tessera_sanitaria']);
  const ocrDocs = docs.filter((d) => OCR_TYPES.has((d.tipo_documento || '').toLowerCase()));

  return (
    <>
      {/* OCR extraction cards for identity docs */}
      {ocrDocs.length > 0 && (
        <div className="space-y-3">
          {ocrDocs.map((d) => (
            <DocumentOcrCard
              key={`ocr-${d.id}`}
              doc={d}
              clientId={clientId}
              onUpdated={fetchDocs}
              onClientFilled={onClientRefresh}
            />
          ))}
        </div>
      )}
      <Card data-testid="contract-documents-preview">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-4 h-4 text-sky-600" />
            Documenti caricati
            <span className="text-xs font-normal text-slate-400">({docs.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {docs.map((doc) => {
              const meta = getFileMeta(doc.nome_file || doc.filename);
              const Icon = meta.icon;
              const isPreviewable = meta.kind === 'image' || meta.kind === 'pdf';
              return (
                <div
                  key={doc.id}
                  className="group relative border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden bg-white dark:bg-slate-800 hover:shadow-md transition"
                  data-testid={`doc-preview-${doc.id}`}
                >
                  <button
                    type="button"
                    onClick={() => openPreview(doc)}
                    className="w-full aspect-[4/3] bg-slate-50 dark:bg-slate-900 flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
                    title={isPreviewable ? 'Anteprima' : 'Scarica'}
                  >
                    {meta.kind === 'image' ? (
                      <img
                        src={doc.url ? `${API_URL}${doc.url}` : `${API_URL}/api/documents/${doc.id}/download`}
                        alt={doc.nome_file}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    ) : (
                      <div className={`p-3 rounded-lg ${meta.color}`}>
                        <Icon className="w-8 h-8" />
                      </div>
                    )}
                  </button>
                  <div className="p-2 text-xs">
                    <p className="font-medium text-slate-800 dark:text-slate-100 truncate" title={doc.nome_file}>
                      {doc.nome_file || doc.filename}
                    </p>
                    <div className="flex items-center justify-between mt-1 text-[10px] text-slate-400">
                      <span className="uppercase font-semibold">{getExt(doc.nome_file || doc.filename)}</span>
                      <span>{formatSize(doc.size)}</span>
                    </div>
                    {doc.tipo_documento && (
                      <p className="text-[10px] text-slate-500 mt-1 truncate">
                        {TIPO_LABELS[doc.tipo_documento] || doc.tipo_documento}
                      </p>
                    )}
                    <p className="text-[10px] text-slate-400 mt-0.5">{formatDate(doc.created_at)}</p>
                  </div>
                  <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                    {isPreviewable && (
                      <button
                        type="button"
                        onClick={() => openPreview(doc)}
                        className="p-1 rounded bg-white/90 dark:bg-slate-900/90 hover:bg-sky-100 text-sky-600 shadow-sm"
                        title="Anteprima"
                        data-testid={`doc-preview-btn-${doc.id}`}
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleDownload(doc)}
                      className="p-1 rounded bg-white/90 dark:bg-slate-900/90 hover:bg-emerald-100 text-emerald-600 shadow-sm"
                      title="Scarica"
                      data-testid={`doc-download-btn-${doc.id}`}
                    >
                      <Download className="w-3.5 h-3.5" />
                    </button>
                    {canDelete && (
                      <button
                        type="button"
                        onClick={() => handleDelete(doc.id)}
                        className="p-1 rounded bg-white/90 dark:bg-slate-900/90 hover:bg-rose-100 text-rose-600 shadow-sm"
                        title="Elimina"
                        data-testid={`doc-delete-btn-${doc.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Preview Modal */}
      <Dialog open={!!preview} onOpenChange={(open) => !open && closePreview()}>
        <DialogContent className="max-w-4xl w-full max-h-[92vh] p-0 overflow-hidden flex flex-col">
          <DialogHeader className="p-4 pb-2 border-b flex-shrink-0">
            <DialogTitle className="flex items-center justify-between gap-3">
              <span className="truncate text-sm sm:text-base">
                {preview?.doc?.nome_file || 'Anteprima'}
              </span>
              <div className="flex items-center gap-1 shrink-0">
                {preview?.blobUrl && (
                  <a
                    href={preview.blobUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
                    title="Apri in nuova scheda"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
                {preview?.doc && (
                  <button
                    type="button"
                    onClick={() => handleDownload(preview.doc)}
                    className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
                    title="Scarica"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                )}
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-auto bg-slate-100 dark:bg-slate-900 flex items-center justify-center">
            {preview?.loading ? (
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            ) : preview?.kind === 'image' ? (
              <img
                src={preview.blobUrl}
                alt={preview.doc?.nome_file}
                className="max-w-full max-h-full object-contain"
              />
            ) : preview?.kind === 'pdf' ? (
              <iframe
                src={preview.blobUrl}
                title={preview.doc?.nome_file}
                className="w-full h-[80vh] border-0"
              />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ContractDocumentsPreview;
